import { usePrefs, useFlags } from 'wsm-common-data';

/**
 * Derives the pricing configuration for a vehicle card from the widget prefs,
 * the full-price-stack feature flag, and the vehicle's raw pricing. Keeps this
 * logic out of the Vehicle component body and independently unit-testable.
 *
 * @param {object} pricing raw vehicle pricing (WIS shape: dprice/eprice + rest)
 * @returns {{
 *   priceMode: string,
 *   normalizedPricing: object,
 *   hasPriceStack: boolean,
 *   isFullStack: boolean,
 *   isDisplayingEPrice: boolean
 * }}
 */
export const useVehiclePricing = (pricing) => {
	const { showPrice: showPricePref = 'final', displayStyle } = usePrefs();
	const flags = useFlags();

	// Feature flag gating the full-price-stack functionality. When disabled,
	// the widget reverts to the previous behavior (final price only) — showPrice
	// modes (collapsed/full) and the shared price-stack component are bypassed.
	const fullPriceStackEnabled = Boolean(
		flags?.['ws-rec-vehicles-full-price-stack-enabled']
	);
	const isNavFragment = displayStyle !== 'Standard Horizontal';

	// The Navigation style always shows only the final price, so showPrice is
	// ignored there. When the flag is off, showPrice is also ignored (previous
	// behavior = final price only).
	const priceMode =
		isNavFragment || !fullPriceStackEnabled ? 'final' : showPricePref;

	// Preserve the whole pricing object (lease, loan, retailPrice, ePriceStatus,
	// etc.), only normalizing the stack casing — WIS returns `dprice`/`eprice`
	// while the shared component expects `dPrice`/`ePrice`. Mirrors
	// ws-inv-listing's pricingMap so PricingDetail receives the complete data set.
	const normalizedPricing = {
		...pricing,
		dPrice: pricing?.dPrice ?? pricing?.dprice,
		ePrice: pricing?.ePrice ?? pricing?.eprice
	};

	const hasPriceStack =
		(normalizedPricing.dPrice?.length ?? 0) > 0 ||
		(normalizedPricing.ePrice?.length ?? 0) > 0;

	// Fall back to the final-price presentation when the stack is unavailable.
	const isFullStack =
		(priceMode === 'full' || priceMode === 'collapsed') && hasPriceStack;

	// TODO: derive isDisplayingEPrice from the vehicle's ePrice status / site
	// preference once ePrice handling is added; dPrice for now.
	const isDisplayingEPrice = false;

	return {
		priceMode,
		normalizedPricing,
		hasPriceStack,
		isFullStack,
		isDisplayingEPrice
	};
};
